/**
 * HarvardATSClassicTemplate.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ATS Rating: 100 / 100 — Perfect
 * Style: Harvard-inspired, left-aligned, single-column, no graphics
 * Best For: Any role, any ATS system
 *
 * Now uses UniversalSectionRenderer for all 14 sections.
 * Template only defines header + style tokens.
 */

import React from 'react';
import EditableField from '../components/EditableField';
import {
  normalizeTemplateData,
  ContactRow, EmptyState, DS,
} from './templateDesignSystem';
import UniversalSectionRenderer from './UniversalSectionRenderer';

export const ATS_META = {
  atsRating: 100,
  atsLabel: 'ATS Perfect',
  bestFor: 'Any role, any ATS system',
  compatibility: 'Works with Workday, Taleo, Greenhouse, Lever, iCIMS',
  recommendedFor: 'All professionals, IIT/NIT graduates, MBA applicants',
};

const SECTION_ORDER = [
  'summary', 'experience', 'internships', 'projects',
  'education', 'skills', 'certifications', 'achievements',
  'awards', 'languages', 'publications', 'volunteer',
  'interests', 'references', 'extracurricular',
];

function HarvardATSClassicTemplate({
  resumeData,
  accentColor = '#1a1a2e',
  fontScale = 1,
  editMode = false,
  onFieldUpdate = () => {},
  onDeleteBullet = null,
  onDeleteSkill = null,
}) {
  if (!resumeData) return <EmptyState />;
  const d = normalizeTemplateData(resumeData);
  const { name, role } = d;

  const scale = fontScale || 1;
  const fs = (base) => `${parseFloat(base) * scale}px`;

  return (
    <div
      id="resume"
      style={{
        background: '#fff',
        color: '#111',
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: fs(DS.fs.body),
        lineHeight: 1.5,
        padding: `${DS.sp.pageY} ${DS.sp.pageX}`,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* ─── Header ─── */}
      <header style={{ marginBottom: 12, borderBottom: `2px solid ${accentColor}`, paddingBottom: 8 }}>
        <EditableField
          id="name"
          editMode={editMode}
          value={name || ''}
          onSave={v => onFieldUpdate('name', v)}
          tag="h1"
          style={{ fontSize: fs(DS.fs.name), fontWeight: 800, color: '#111', margin: 0, letterSpacing: '-0.3px' }}
          placeholder="Your Name"
        />
        {(role || editMode) && (
          <EditableField
            id="role"
            editMode={editMode}
            value={role || ''}
            onSave={v => onFieldUpdate('role', v)}
            tag="p"
            style={{ fontSize: fs('11px'), fontWeight: 600, color: accentColor, margin: '2px 0 0', textTransform: 'uppercase', letterSpacing: '0.8px' }}
            placeholder="Your Professional Title"
          />
        )}
        <ContactRow data={d} accentColor={accentColor} editMode={editMode} onFieldUpdate={onFieldUpdate} />
      </header>

      {/* ─── All sections via Universal Renderer ─── */}
      <UniversalSectionRenderer
        resumeData={{ ...resumeData, section_order: resumeData.section_order || SECTION_ORDER }}
        templateConfig={{ sectionOrder: SECTION_ORDER }}
        accentColor={accentColor}
        fontScale={fontScale}
        editMode={editMode}
        onFieldUpdate={onFieldUpdate}
        onDeleteBullet={onDeleteBullet}
        onDeleteSkill={onDeleteSkill}
      />
    </div>
  );
}

export default HarvardATSClassicTemplate;
